/**
 * Main simulation loop for the 2D rocket landing engine.
 *
 * Orchestrates: controller queries, action clamping, fuel-based thrust gating,
 * RK4 integration with event splitting (fuel exhaustion, ground impact),
 * telemetry emission at the configured output interval.
 *
 * Deterministic: identical config + initial state + controller -> identical result.
 * Independent of React/browser APIs; runnable in Node.
 */

import type {
  Environment,
  RocketParams,
  RocketState,
  ControlAction,
  SimulationConfig,
  SimulationResult,
  TelemetryPoint,
  Controller,
  LandingResult,
} from "./types";
import { isFiniteState } from "./types";
import { validateSimulationConfig } from "./rocket";
import { computeDerivatives } from "./dynamics";
import { integrateStep } from "./integrator";
import { deriveTelemetry, deriveImpactTelemetry } from "./telemetry";

/**
 * Default configuration values for the educational simulation.
 * Documented assumptions:
 *   - Rocket roughly the size of a small sounding rocket / lander prototype.
 *   - Thrust-to-weight ratio at launch ~1.5 (enough to hover and maneuver).
 *   - Fuel flow chosen so a full-throttle burn lasts ~20 seconds.
 *   - Drag coefficient and reference area produce noticeable but not dominant drag.
 */
export const DefaultConfig: SimulationConfig = {
  environment: {
    gravity: 9.81,
    seaLevelDensity: 1.225,
    scaleHeight: 8500,
    density: (z: number) => 1.225 * Math.exp(-Math.max(z, 0) / 8500),
  },
  rocket: {
    dryMass: 1000,
    maxFuel: 5000,
    maxThrust: 150000,
    fuelFlowRate: 250,
    dragCoefficient: 0.5,
    referenceArea: 10,
    inertiaY: 5000,
    centerOfMassOffset: 5,
  },
  initialState: {
    t: 0,
    x: 0,
    z: 500,
    vx: 0,
    vz: 0,
    fuel: 5000,
    theta: 0,
    omega: 0,
  },
  maxGimbalAngle: 0.2,
  physicsDt: 0.05,
  outputDt: 0.1,
  maxDuration: 300,
  landingTargetX: 0,
  maxTouchdownVerticalSpeed: 5,
  maxTouchdownHorizontalSpeed: 2,
  maxHorizontalError: 10,
  maxTouchdownAttitude: 0.17, // ~9.7 degrees
  maxTouchdownAngularRate: 0.5,
  descentRate: 15,
  flareAltitude: 100,
};

/**
 * Clamp a control action to allowed ranges.
 */
function clampAction(
  action: ControlAction,
  maxGimbalAngle: number
): ControlAction {
  return {
    throttle: Math.min(1, Math.max(0, action.throttle)),
    gimbal: Math.min(maxGimbalAngle, Math.max(-maxGimbalAngle, action.gimbal)),
  };
}

/**
 * Run a complete simulation.
 *
 * @param config - Validated simulation configuration.
 * @param controller - Controller that generates actions from state.
 * @returns Complete simulation result including telemetry and landing outcome.
 */
export function runSimulation(
  config: SimulationConfig,
  controller: Controller
): SimulationResult {
  validateSimulationConfig(config);

  const {
    environment,
    rocket,
    initialState,
    maxGimbalAngle,
    physicsDt,
    outputDt,
    maxDuration,
    landingTargetX,
    maxTouchdownVerticalSpeed,
    maxTouchdownHorizontalSpeed,
    maxHorizontalError,
    maxTouchdownAttitude,
    maxTouchdownAngularRate,
  } = config;

  const env: Environment = environment;
  const params: RocketParams = rocket;

  controller.reset();

  const telemetry: TelemetryPoint[] = [];
  let state: RocketState = { ...initialState };
  let impacted = false;
  let impactTime: number | null = null;
  let landingSuccess = false;
  let touchdownVx = 0;
  let touchdownVz = 0;
  let horizontalError = 0;

  let t = initialState.t;
  let nextOutputT = initialState.t;

  // Emit initial telemetry point.
  const initialAction = clampAction(controller.compute(state, env, physicsDt), maxGimbalAngle);
  const effectiveThrottle = state.fuel <= 0 ? 0 : initialAction.throttle;
  const clampedInitialAction: ControlAction = {
    throttle: effectiveThrottle,
    gimbal: initialAction.gimbal,
  };
  const initialDeriv = computeDerivatives(state, clampedInitialAction, env, params);
  const initialMass = rocket.dryMass + state.fuel;
  telemetry.push(
    deriveTelemetry(
      state,
      clampedInitialAction,
      initialDeriv.dvx * initialMass,
      initialDeriv.dvz * initialMass,
      initialMass
    )
  );

  const stepsPerOutput = Math.round(outputDt / physicsDt);
  let stepsSinceOutput = 0;

  while (!impacted && t < maxDuration) {
    // Ask controller for action based on current state.
    const commandedAction = clampAction(controller.compute(state, env, physicsDt), maxGimbalAngle);

    // Determine effective throttle based on fuel.
    const effectiveThrottle = state.fuel <= 0 ? 0 : commandedAction.throttle;
    const appliedAction: ControlAction = {
      throttle: effectiveThrottle,
      gimbal: commandedAction.gimbal,
    };

    // Integrate one physics step with event handling.
    const stepResult = integrateStep(state, appliedAction, env, params, physicsDt);
    state = stepResult.state;

    if (stepResult.impacted) {
      impacted = true;
      impactTime = stepResult.impactTime ?? state.t;
      break;
    }

    t += physicsDt;
    stepsSinceOutput++;

    // Emit telemetry at output interval.
    if (stepsSinceOutput >= stepsPerOutput) {
      const deriv = computeDerivatives(state, appliedAction, env, params);
      const mass = rocket.dryMass + state.fuel;
      telemetry.push(
        deriveTelemetry(state, appliedAction, deriv.dvx * mass, deriv.dvz * mass, mass)
      );
      stepsSinceOutput = 0;
      nextOutputT += outputDt;
    }

    // NaN / Infinity guard: stop if state becomes invalid.
    if (!isFiniteState(state)) {
      break;
    }
  }

  // Build landing result.
  if (impacted) {
    const deriv = computeDerivatives(
      state,
      { throttle: 0, gimbal: 0 },
      env,
      params
    );
    const mass = rocket.dryMass + state.fuel;
    const { landing } = deriveImpactTelemetry(
      state,
      { throttle: 0, gimbal: 0 },
      deriv.dvx * mass,
      deriv.dvz * mass,
      mass,
      impactTime!,
      landingTargetX,
      maxTouchdownVerticalSpeed,
      maxTouchdownHorizontalSpeed,
      maxHorizontalError,
      maxTouchdownAttitude,
      maxTouchdownAngularRate
    );
    landingSuccess = landing.landingSuccess;
    touchdownVx = landing.touchdownVx;
    touchdownVz = landing.touchdownVz;
    horizontalError = landing.horizontalError;
  } else {
    // Reached max duration without impact.
    landingSuccess = false;
    touchdownVx = state.vx;
    touchdownVz = state.vz;
    horizontalError = Math.abs(state.x - landingTargetX);
  }

  const finalState: RocketState | null = state;

  const landing: LandingResult = {
    landingSuccess,
    touchdownVx,
    touchdownVz,
    touchdownSpeed: Math.sqrt(touchdownVx * touchdownVx + touchdownVz * touchdownVz),
    horizontalError,
    impactTime: impacted ? (impactTime ?? state.t) : state.t,
  };

  return {
    telemetry,
    finalState,
    landing,
  };
}
